import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Collapse,
  IconButton,
  Chip,
  LinearProgress,
  Autocomplete,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Warning,
  CheckCircle,
  ErrorOutline,
} from '@mui/icons-material';
import {
  getEffectiveCoverage,
  EffectiveCoverageData,
  CoverageDevice,
  CoverageSupply,
} from '../services/api';

type Order = 'asc' | 'desc';

const getCoverageColor = (coverage: number): string => {
  if (coverage <= 5) return '#4caf50';      // Green - at or below standard
  if (coverage <= 8) return '#8bc34a';      // Light green - slightly above
  if (coverage <= 12) return '#ff9800';     // Orange - watch
  if (coverage <= 18) return '#f44336';     // Red - high
  return '#9c27b0';                          // Purple - very high
};

const getCoverageLabel = (coverage: number): string => {
  if (coverage <= 5) return 'Normal';
  if (coverage <= 8) return 'Aceptable';
  if (coverage <= 12) return 'Elevado';
  if (coverage <= 18) return 'Alto';
  return 'Muy Alto';
};

const getPerformanceIcon = (performance: number) => {
  if (performance >= 80) return <CheckCircle sx={{ color: '#4caf50', fontSize: 18 }} />;
  if (performance >= 50) return <Warning sx={{ color: '#ff9800', fontSize: 18 }} />;
  return <ErrorOutline sx={{ color: '#f44336', fontSize: 18 }} />;
};

const colourLabels: Record<string, string> = {
  BLACK: 'Negro',
  CYAN: 'Cian',
  MAGENTA: 'Magenta',
  YELLOW: 'Amarillo',
};

const colourDots: Record<string, string> = {
  BLACK: '#000',
  CYAN: '#00bcd4',
  MAGENTA: '#e91e63',
  YELLOW: '#ffc107',
};

function SupplyRow({ supply }: { supply: CoverageSupply }) {
  const coverageColor = getCoverageColor(supply.effective_coverage);
  return (
    <TableRow sx={{ '& td': { borderBottom: 'none', py: 0.5 } }}>
      <TableCell sx={{ pl: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: colourDots[supply.colour] || '#999',
              border: supply.colour === 'BLACK' ? '1px solid #ccc' : 'none',
            }}
          />
          <Typography variant="body2">
            {colourLabels[supply.colour] || supply.colour}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">{supply.sku}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{supply.target_yield.toLocaleString()}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{supply.achieved_yield.toLocaleString()}</Typography>
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
          {getPerformanceIcon(supply.performance)}
          <Typography variant="body2">{supply.performance}%</Typography>
        </Box>
      </TableCell>
      <TableCell align="right">
        <Chip
          label={`${supply.effective_coverage}%`}
          size="small"
          sx={{
            backgroundColor: coverageColor,
            color: '#fff',
            fontWeight: 600,
            minWidth: 60,
          }}
        />
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={supply.percent_left}
            sx={{
              width: 60,
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: supply.percent_left > 20 ? '#4caf50' : supply.percent_left > 10 ? '#ff9800' : '#f44336',
              },
            }}
          />
          <Typography variant="body2" color="text.secondary">{supply.percent_left}%</Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
}

function DeviceRow({ device }: { device: CoverageDevice }) {
  const [open, setOpen] = useState(false);
  const coverageColor = getCoverageColor(device.avg_effective_coverage);
  const coverageLabel = getCoverageLabel(device.avg_effective_coverage);

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
        onClick={() => setOpen(!open)}
      >
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small">
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>{device.serial_number}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{device.client_name}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">{device.model}</Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2">{device.supplies.length}</Typography>
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
            <Chip
              label={`${device.avg_effective_coverage}%`}
              size="small"
              sx={{
                backgroundColor: coverageColor,
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.85rem',
                minWidth: 65,
              }}
            />
            <Typography variant="caption" sx={{ color: coverageColor, fontWeight: 500 }}>
              {coverageLabel}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ pl: 6, fontWeight: 600 }}>Color</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Target Yield</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Achieved Yield</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Performance</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Eff. Coverage</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Nivel Restante</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {device.supplies.map((s, i) => (
                    <SupplyRow key={i} supply={s} />
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const EffectiveCoverage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<EffectiveCoverageData | null>(null);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState<'avg_effective_coverage' | 'serial_number' | 'client_name'>('avg_effective_coverage');
  const [order, setOrder] = useState<Order>('desc');
  const [clientFilter, setClientFilter] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getEffectiveCoverage();
        setData(result);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Error fetching coverage data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Unique client names for filter
  const clientNames = useMemo(() => {
    if (!data) return [];
    const names = Array.from(new Set(data.devices.map(d => d.client_name))).sort();
    return names;
  }, [data]);

  // Filter and sort
  const filteredDevices = useMemo(() => {
    if (!data) return [];
    let devices = data.devices;

    if (search) {
      const q = search.toLowerCase();
      devices = devices.filter(
        d =>
          d.serial_number.toLowerCase().includes(q) ||
          d.client_name.toLowerCase().includes(q) ||
          d.model.toLowerCase().includes(q)
      );
    }

    if (clientFilter) {
      devices = devices.filter(d => d.client_name === clientFilter);
    }

    devices = [...devices].sort((a, b) => {
      const aVal = a[orderBy];
      const bVal = b[orderBy];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return order === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return order === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    return devices;
  }, [data, search, clientFilter, orderBy, order]);

  const handleSort = (field: typeof orderBy) => {
    if (orderBy === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder(field === 'avg_effective_coverage' ? 'desc' : 'asc');
    }
  };

  // Recompute summary from filtered devices
  const summary = useMemo(() => {
    const allCoverages: number[] = [];
    let highCount = 0;
    for (const dev of filteredDevices) {
      for (const s of dev.supplies) {
        if (s.effective_coverage > 0) {
          allCoverages.push(s.effective_coverage);
          if (s.effective_coverage > 10) highCount++;
        }
      }
    }
    const avg = allCoverages.length > 0
      ? allCoverages.reduce((a, b) => a + b, 0) / allCoverages.length
      : 0;
    return {
      total_devices: filteredDevices.length,
      total_supplies_analyzed: allCoverages.length,
      avg_effective_coverage: Math.round(avg * 10) / 10,
      high_coverage_count: highCount,
      high_coverage_threshold: 10,
    };
  }, [filteredDevices]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 2 }}>
        <CircularProgress size={48} />
        <Typography color="text.secondary">
          Consultando consumibles del portal SDS/EKM...
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Esto puede tomar 1-2 minutos para procesar todos los equipos
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Effective Coverage
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Cobertura efectiva real por equipo, calculada desde datos SDS/EKM.
        El estandar de la industria es 5%. Valores superiores indican mayor consumo de toner por pagina.
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700}>{summary.total_devices}</Typography>
              <Typography variant="body2" color="text.secondary">Equipos Analizados</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700}>{summary.total_supplies_analyzed}</Typography>
              <Typography variant="body2" color="text.secondary">Suministros Analizados</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{ color: getCoverageColor(summary.avg_effective_coverage) }}
              >
                {summary.avg_effective_coverage}%
              </Typography>
              <Typography variant="body2" color="text.secondary">Cobertura Promedio</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="error">
                {summary.high_coverage_count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cobertura &gt; {summary.high_coverage_threshold}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {[
          { label: 'Normal (<=5%)', color: '#4caf50' },
          { label: 'Aceptable (5-8%)', color: '#8bc34a' },
          { label: 'Elevado (8-12%)', color: '#ff9800' },
          { label: 'Alto (12-18%)', color: '#f44336' },
          { label: 'Muy Alto (>18%)', color: '#9c27b0' },
        ].map(({ label, color }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: 1, backgroundColor: color }} />
            <Typography variant="caption">{label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar por serial, cliente o modelo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 350 }}
        />
        <Autocomplete
          size="small"
          options={clientNames}
          value={clientFilter}
          onChange={(_, val) => setClientFilter(val)}
          renderInput={(params) => <TextField {...params} placeholder="Filtrar por cliente" />}
          sx={{ width: 300 }}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ width: 40 }} />
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'serial_number'}
                  direction={orderBy === 'serial_number' ? order : 'asc'}
                  onClick={() => handleSort('serial_number')}
                >
                  Serial
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'client_name'}
                  direction={orderBy === 'client_name' ? order : 'asc'}
                  onClick={() => handleSort('client_name')}
                >
                  Cliente
                </TableSortLabel>
              </TableCell>
              <TableCell>Modelo</TableCell>
              <TableCell align="center">Supplies</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'avg_effective_coverage'}
                  direction={orderBy === 'avg_effective_coverage' ? order : 'desc'}
                  onClick={() => handleSort('avg_effective_coverage')}
                >
                  Cobertura Efectiva
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDevices.map((device) => (
              <DeviceRow key={device.device_id} device={device} />
            ))}
            {filteredDevices.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No se encontraron equipos con datos de cobertura
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Mostrando {filteredDevices.length} de {data.devices.length} equipos.
        Cobertura calculada con formula: 5% x (Target Yield / Achieved Yield).
        Solo se incluyen toners/tintas con mas de 5% de uso.
      </Typography>
    </Box>
  );
};

export default EffectiveCoverage;
